import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useSnackbar } from 'notistack'

import { useTranslations } from '@/hooks/useTranslations'

import type { MutationFunction, QueryKey } from '@tanstack/react-query'

export interface ApiMutationProps<TData = unknown, TError = unknown, TVariables = void, TContext = unknown> {
  /** Mutation function */
  func: MutationFunction<TData, TVariables>
  /** A cacheKey that should be invalidated after the call finishes. */
  cacheKey: QueryKey | QueryKey[]
  /** Translation key base for the snackbar message. */
  translationKeyBase?: string
  invalidateQueries?: boolean
  onSuccess?: (data: TData, variables: TVariables, context: TContext | undefined) => Promise<unknown> | void
  onError?: (error: TError, variables: TVariables, context: TContext | undefined) => Promise<unknown> | void
  onSettled?: (
    data: TData | undefined,
    error: TError | null,
    variables: TVariables,
    context: TContext | undefined
  ) => Promise<unknown> | void
}

export const useApiMutation = <TData = unknown, TError = unknown, TVariables = void, TContext = unknown>({
  func,
  cacheKey,
  translationKeyBase,
  invalidateQueries = true,
  onSuccess,
  onSettled,
  onError,
}: ApiMutationProps<TData, TError, TVariables, TContext>) => {
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()
  const { translate } = useTranslations()

  return useMutation<TData, TError, TVariables, TContext>({
    mutationFn: func,
    onSuccess: (data, variables, context) => {
      // Invalidate queries only on success
      if (invalidateQueries) {
        if (Array.isArray(cacheKey) && Array.isArray(cacheKey[0])) {
          cacheKey.forEach((key) => {
            queryClient.invalidateQueries({ queryKey: key })
          })
        } else {
          queryClient.invalidateQueries({ queryKey: cacheKey })
        }
      }

      void onSuccess?.(data, variables, context)
      if (translationKeyBase) {
        enqueueSnackbar(translate(`${translationKeyBase}.success`), {
          variant: 'success',
        })
      }
    },
    onError:
      onError !== undefined
        ? onError
        : async () => {
            if (translationKeyBase) {
              enqueueSnackbar(translate(`${translationKeyBase}.error`), {
                variant: 'error',
              })
            }
          },
    onSettled: (
      data: TData | undefined,
      error: TError | null,
      variables: TVariables,
      context: TContext | undefined
    ) => {
      // Run the onSettled function if provided
      void onSettled?.(data, error, variables, context)
    },
    // This hook always shows its own toast (translated, or the generic fallback above),
    // so the global MutationCache toast in ReactQueryClientProvider would always be a duplicate.
    meta: { shouldSuppressErrorToast: () => true },
  })
}
